import { Component, Mixins, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { MagicConfigMixin, SessionMixin } from '@gig/magic-common';
import { magic } from '@gig/magic-sdk';
import { IGetUserRewardsResponse } from '@gig/magic-sdk/dist/adapter/rewards/getUserRewards';
import {
    IRewardHistoryResponse,
    RewardReason,
} from '@gig/magic-sdk/dist/adapter/rewards/history';
import {
    TIER_LEVELS,
    RUBY_TIER_ACHIEVED_REWARD,
} from '../loyalty.constants';
import {
    LOYALTY_NAMESPACE,
    getters as loyaltyGetters,
} from '../../../store/modules/loyalty';
import { getTierCode, isBlackDiamondUser } from '../loyalty.utils';
import { RouteName } from '../../../routes/routes.constants';

const loyaltyModule = namespace(LOYALTY_NAMESPACE);

@Component({
    name: 'loyalty-header',
})
export default class LoyaltyHeader extends Mixins(
    MagicConfigMixin,
    SessionMixin
) {
    RouteName = RouteName;
    /**
     * Boolean flag to track data fetching in progress
     */
    isLoading = false;

    /**
     * User rewards data
     */
    userRewardsData: IGetUserRewardsResponse | null = null;

    /**
     * User history rewards data
     */
    userHistoryRewardsData: IRewardHistoryResponse | null = null;

    /**
     * has the user made at least 1 deposit
     */
    hasDeposit: boolean = false;

    @loyaltyModule.Getter('progress')
    loyaltyProgress!: ReturnType<typeof loyaltyGetters.progress>;

    @loyaltyModule.Getter('points')
    loyaltyPoints!: ReturnType<typeof loyaltyGetters.points>;

    @loyaltyModule.Getter('level')
    loyaltyLevel!: ReturnType<typeof loyaltyGetters.level>;

    @loyaltyModule.Getter('pointsNextLevel')
    pointsNextLevel!: ReturnType<typeof loyaltyGetters.pointsNextLevel>;

    @loyaltyModule.Getter('tier')
    loyaltyTier!: ReturnType<typeof loyaltyGetters.tier>;

    @Watch('user', { immediate: true, deep: true })
    @Watch('loyaltyTier')
    initData() {
        if (!this.isLoggedIn) {
            this.clearState();
            return;
        }
        this.isLoading = true;
        this.fetchData();
    }

    get loyaltyCode() {
        if (this.loyaltyTier === null) {
            return '';
        }
        return getTierCode(this.loyaltyTier);
    }

    get isBlackDiamondTier() {
        return this.loyaltyTier === TIER_LEVELS.BLACK_DIAMOND;
    }

    get pointsToNextLevel(): number {
        if (this.pointsNextLevel === null || this.loyaltyPoints === null) {
            return 0;
        }

        return this.pointsNextLevel - this.loyaltyPoints;
    }

    get coins() {
        return new Intl.NumberFormat().format(
            this?.userRewardsData?.rewards?.echoes || 0
        );
    }

    get hasClaimedRubyReward() {
        if (!this.userHistoryRewardsData) {
            return false;
        }
        const rubyRewardHistory =
            this.userHistoryRewardsData?.rewardHistory?.[
                RUBY_TIER_ACHIEVED_REWARD
            ] || [];
        return rubyRewardHistory.find(
            (row) => row.reason === RewardReason.EXCHANGE
        );
    }

    get isRubyTier() {
        return this.loyaltyTier === TIER_LEVELS.RUBY;
    }

    get shouldDisplayGenericHeader() {
        return (
            !this.user ||
            !this.loyaltyPoints ||
            (this.loyaltyPoints === 0 &&
                !isBlackDiamondUser(this.user) &&
                !this.hasDeposit)
        );
    }

    private async fetchData() {
        try {
            this.userRewardsData = await this.fetchUserRewards();
            if (this.loyaltyTier === TIER_LEVELS.RUBY) {
                this.userHistoryRewardsData = await this.fetchUserRewardsHistory();
            }
            const lifetimeDeposits = await this.fetchLifetimeDeposits();
            this.hasDeposit = lifetimeDeposits.lifetimeDeposits > 0;
        } catch (e) {
            console.error('Error loading data:', e);
        } finally {
            this.isLoading = false;
        }
    }

    private clearState() {
        this.userRewardsData = null;
        this.hasDeposit = false;
    }

    private async fetchUserRewards() {
        return magic.methods.rewards.getUserRewards({ order: 'asc' });
    }

    private async fetchLifetimeDeposits() {
        return magic.methods.wallet.getLifetimeDeposits();
    }

    private async fetchUserRewardsHistory() {
        const rewardsHistoryPromise: ReturnType<typeof magic.methods.rewards.history> = magic.invokeCached(
            30,
            magic.methods.rewards,
            'history'
        )({});
        return rewardsHistoryPromise;
    }
}
